import type { FilterField } from '@/shared/components/filter/types'
import type { FieldSchemaModel } from '@/shared/generated'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'

import { useEffect, useMemo } from 'react'
import { filter, map, pick, pipe } from 'lodash/fp'
import { PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	isAnchorStageField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'

import { getLocalization } from '@/shared/components/filter/localization'
import { RHFSelect, RHFSwitch } from '@/shared/components/HookForm'
import Combobox from '@/shared/components/query-builder/components/Combobox'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { HiddenOnContext } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'

import { getPriceDistanceTypeOptions } from '../lib/priceDistanceTypeOptions'

export const PriceDistanceTab = () => {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.anchoringAnchorStrategyOpenPriceDistance()
	}, [])
	const { formState, control } = useFormContext()
	const error = formState.errors?.settings as ErrorSchema
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'settings.indexes.columns',
	})
	const fieldsQuery = useColumnsSchemaQuery<FilterField[]>(
		HiddenOnContext.AnchorIndexing,
		{
			select: pipe([
				filter((item) => isAnchorStageField(item as FieldSchemaModel)),
				map((schema: FieldSchemaModel) => toAdaptedSchema(schema, t)),
			]),
		}
	)
	const columnsOptions = useMemo(
		() => map(pick(['value', 'label']), fieldsQuery.data),
		[fieldsQuery.data]
	)
	const addPriceColumn = () => {
		ampli.anchoringAnchorStrategyAddPriceColumn()
		append('')
	}
	const removePriceColumn = (index: number) => {
		remove(index)
	}

	if (fieldsQuery.isPending) {
		return (
			<div className='flex flex-col space-y-2'>
				<Skeleton className='h-10 w-full' />
				<Skeleton className='h-10 w-full' />
			</div>
		)
	}

	return (
		<div className='space-y-6'>
			<Separator className='mb-4' />
			<RHFSelect
				className='w-96'
				label={t(
					'anchoring_management.strategy.tab.price_distance.index_type.title'
				)}
				name='settings.indexes.index_type'
				defaultValue='mul'
				options={getPriceDistanceTypeOptions(t)}
			/>
			<div className='space-y-2'>
				<Label className='bold text-sm'>
					{t('anchoring_management.strategy.tab.price_distance.columns.title')}
				</Label>
				<div className='space-y-2'>
					{fields?.map((field, i) => (
						<div key={field.id} className='flex w-full items-start space-x-2'>
							<div className='pt-1'>
								<span className='flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 p-2 text-sm text-gray-500'>
									{i + 1}
								</span>
							</div>
							<Controller
								control={control}
								name={`settings.indexes.columns.${i}`}
								render={({ field }) => (
									<Combobox
										name={`price-distance-${i}`}
										value={field.value as string}
										onChange={field.onChange}
										options={
											columnsOptions as { value: string; label: string }[]
										}
										placeholder={t(
											'anchoring_management.strategy.tab.price_distance.price_column.placeholder'
										)}
										locale={getLocalization(t)}
										className='w-[400px]'
									/>
								)}
							/>
							<Button
								variant='ghost'
								size='icon'
								onClick={() => removePriceColumn(i)}
							>
								<XIcon />
							</Button>
						</div>
					))}
				</div>
				<Button className='mr-auto' variant='ghost' onClick={addPriceColumn}>
					<PlusIcon />
					{t(
						'anchoring_management.strategy.tab.price_distance.add_column.title'
					)}
				</Button>
				{error?.indexes?.columns && (
					<div className='mb-1 text-sm text-destructive'>
						{error?.indexes?.columns?.root?.message ||
							error?.indexes?.columns?.message}
					</div>
				)}
			</div>
			<Separator />
			<div className='space-y-3'>
				<RHFSwitch
					name='settings.general.propagate_generated_anchors'
					label={t(
						'anchoring_management.strategy.general.propagate_generated_anchors',
						{ defaultValue: 'Propagate generated indexes' }
					)}
				/>
				<RHFSwitch
					name='settings.general.cleanup_generated_anchors'
					label={t(
						'anchoring_management.strategy.general.cleanup_generated_anchors',
						{ defaultValue: 'Cleanup generated indexes' }
					)}
				/>
			</div>
		</div>
	)
}
